import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGUAGES,
  changeAppLanguage,
  getLanguageOption,
  normalizeLanguageCode,
} from '../../utils/languages';
import FlagIcon from './FlagIcon';

const MENU_WIDTH = 248;
const MENU_GAP = 6;

const LanguageSelector = ({ variant = 'compact', className = '' }) => {
  const { t, i18n } = useTranslation('settings');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: MENU_WIDTH });
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);
  const listId = useId();

  const currentCode = normalizeLanguageCode(i18n.language);
  const current = getLanguageOption(currentCode);
  const isSettings = variant === 'settings';

  const updateMenuPosition = () => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const width = isSettings ? Math.max(rect.width, MENU_WIDTH) : MENU_WIDTH;
    const rtl = document.documentElement.dir === 'rtl';
    let left = rtl ? rect.left : rect.right - width;
    if (isSettings) left = rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

    const estimatedHeight = Math.min(window.innerHeight * 0.7, 264);
    let top = rect.bottom + MENU_GAP;
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estimatedHeight - MENU_GAP);
    }
    setMenuPos({ top, left, width });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, isSettings]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open && activeIndex >= 0) {
      optionRefs.current[activeIndex]?.focus();
    }
  }, [open, activeIndex]);

  const selectLanguage = async (code) => {
    await changeAppLanguage(i18n, code);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const handleButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const index = Math.max(0, SUPPORTED_LANGUAGES.findIndex((lang) => lang.code === currentCode));
      setActiveIndex(index);
      setOpen(true);
    }
  };

  const handleOptionKeyDown = (event, index) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index + 1) % SUPPORTED_LANGUAGES.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index - 1 + SUPPORTED_LANGUAGES.length) % SUPPORTED_LANGUAGES.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(SUPPORTED_LANGUAGES.length - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectLanguage(SUPPORTED_LANGUAGES[index].code);
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  const buttonClass = isSettings
    ? 'flex h-11 w-full min-w-[220px] items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#161f31] px-3.5 text-sm font-medium text-[#e8f0fa] transition-colors hover:border-[#3d8ef0]/40 focus:outline-none focus-visible:border-[#3d8ef0]/70 focus-visible:shadow-[0_0_0_3px_rgba(61,142,240,0.16)]'
    : 'inline-flex h-9 items-center gap-1.5 rounded-full border border-white/22 bg-[#0d1424]/92 px-3 text-xs font-semibold text-white shadow-[0_8px_24px_rgba(5,10,22,0.45)] backdrop-blur-md transition-colors hover:border-[#3d8ef0]/50 hover:bg-[#122036] focus:outline-none focus-visible:border-[#3d8ef0]/70';

  const menu = open ? createPortal(
    <ul
      ref={menuRef}
      id={listId}
      role="listbox"
      aria-label={t('language.title')}
      style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
      className="fixed z-[200000] max-h-[min(70vh,16.5rem)] overflow-y-auto rounded-xl border border-[#3d8ef0]/25 bg-[#0d1424]/96 py-1 shadow-[0_16px_40px_rgba(5,10,22,0.55)] backdrop-blur-md"
      dir="ltr"
    >
      {SUPPORTED_LANGUAGES.map((lang, index) => {
        const selected = lang.code === currentCode;
        return (
          <li key={lang.code} role="presentation">
            <button
              ref={(node) => { optionRefs.current[index] = node; }}
              type="button"
              role="option"
              aria-selected={selected}
              tabIndex={activeIndex === index ? 0 : -1}
              className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                selected
                  ? 'bg-[#3d8ef0]/16 text-white'
                  : 'text-[#c5d6ea] hover:bg-[#3d8ef0]/12 hover:text-white'
              }`}
              dir="ltr"
              onClick={() => selectLanguage(lang.code)}
              onKeyDown={(event) => handleOptionKeyDown(event, index)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <FlagIcon code={lang.code} />
              <span className="w-6 shrink-0 text-[0.72rem] font-semibold tracking-wide text-[#9bb4ce]">
                {lang.shortLabel}
              </span>
              <span aria-hidden="true" className="text-[#6f8bab]">—</span>
              <span className="min-w-0 flex-1 truncate">{lang.nativeLabel || lang.label}</span>
              {selected && <Check className="size-3.5 shrink-0 text-[#5ca3f5]" />}
            </button>
          </li>
        );
      })}
    </ul>,
    document.body
  ) : null;

  return (
    <div ref={rootRef} className={`relative z-30 ${isSettings ? 'w-full max-w-[280px]' : ''} ${className}`.trim()}>
      <button
        ref={buttonRef}
        type="button"
        className={`${buttonClass} no-drag`}
        dir="ltr"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={t('language.title')}
        onClick={() => {
          const index = Math.max(0, SUPPORTED_LANGUAGES.findIndex((lang) => lang.code === currentCode));
          setActiveIndex(index);
          setOpen((prev) => !prev);
        }}
        onKeyDown={handleButtonKeyDown}
      >
        <span className="flex min-w-0 items-center gap-2">
          <FlagIcon code={current.code} />
          {isSettings ? (
            <span className="truncate">{current.nativeLabel || current.label}</span>
          ) : (
            <span className="tracking-wide">{current.shortLabel}</span>
          )}
        </span>
        <ChevronDown className={`size-3.5 shrink-0 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {menu}
    </div>
  );
};

export default LanguageSelector;
